import "primeflex/primeflex.css";
import "primeicons/primeicons.css";
import "primereact/resources/primereact.min.css";
// Leave a space so that the primereact styles are on top (otherwise, the icons throughout the app are broken)
import "core-js/actual/promise";
// Leave a space so that the polyfill is loaded earlier
import { isApiError } from "@core/api/api-error";
import {
	cookiebotManager,
	initializeServicesAndManagers,
	localeManager,
	resetServicesAndManagers,
	serverConfigManager,
	translationsManager,
	userManager,
} from "@core/service-container";
import { hasOwnProperty } from "@core/utils/type-utils";
import { css, Global } from "@emotion/react";
import * as Sentry from "@sentry/react";
import { ErrorType, ServerErrorView } from "@ui/components/common/server-error-view";
import { ToastProvider } from "@ui/components/common/toast-provider";
import { AppRouter } from "@ui/navigation/app-router";
import { Metadata } from "@ui/navigation/metadata";
import { NetworkErrorReporter } from "@ui/pages/network/network-error-reporter";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import "@ui/style/fonts.css";
import "@ui/style/theme.css";
import { isNotNil } from "@utils/assert";
import { useObservable } from "micro-observables";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { IntlProvider } from "react-intl";
import { getLowercaseCountryCodeFromIntlLocale } from "@core/i18n/locale-helper";
import { attachInactivityLogout, resolveInactivityEnabled, resolveInactivityTimeoutMs } from "@core/session/inactivity-session";

export const App = (): JSX.Element => {
	const [servicesInitialized, setServicesInitialized] = useState(false);

	const locale = useObservable(localeManager.locale);
	const translationsStatus = useObservable(translationsManager.translationsStatus);
	const localeWordings = useMemo(
		() => translationsManager.getLocaleWordings(locale),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[locale, servicesInitialized],
	);
	const scriptRef = useRef<HTMLScriptElement | null>(null);

	useEffect(() => {
		const init = async () => {
			try {
				await initializeServicesAndManagers();
			} catch (e) {
				console.error("failed to init services", e);
			} finally {
				setServicesInitialized(true);
			}
		};
		init();
	}, []);

	useEffect(() => {
		if (!servicesInitialized || !translationsStatus.available) {
			return;
		}

		const baseScriptURL = "https://consent.cookiebot.com/uc.js?cbid=815f9805-5c74-41e3-b174-a7e216b7b409&blockingmode=auto";

		let cultureValue: string;
		// Special case for Brazilian Portuguese because it's the only one full locale in Cookiebot
		if (locale.toLowerCase() === "pt-br") {
			cultureValue = "pt-BR";
		} else {
			cultureValue = getLowercaseCountryCodeFromIntlLocale(locale).toUpperCase();
		}

		const fullScriptURL = `${baseScriptURL}&culture=${cultureValue}`;
		const newScript = document.createElement("script");
		scriptRef.current = newScript;
		newScript.id = "Cookiebot";
		newScript.src = fullScriptURL;
		newScript.dataset.culture = cultureValue;
		newScript.async = true;
		document.head.appendChild(newScript);

		return () => {
			const currentScript = scriptRef.current;
			if (currentScript && document.head.contains(currentScript)) {
				currentScript.remove();
			}
		};
	}, [servicesInitialized, translationsStatus.available, locale]);

	return (
		<>
			<Global styles={globalStyles} />
			<IntlProvider locale={locale} messages={localeWordings}>
				<Metadata />
				<ToastProvider>
					<NetworkErrorReporter />
					{servicesInitialized ? <AppContainer /> : null}
				</ToastProvider>
			</IntlProvider>
		</>
	);
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const AppContainer: React.FC = () => {
	const consentStatistics = useObservable(cookiebotManager.consentStatistics);
	const serverConfigStatus = useObservable(serverConfigManager.serverConfigStatus);
	const translationsStatus = useObservable(translationsManager.translationsStatus);
	const currentUser = useObservable(userManager.user);

	const errorObject = useMemo(
		() =>
			typeof translationsStatus.error === "object" && isNotNil(translationsStatus.error)
				? translationsStatus.error
				: {},
		[translationsStatus.error],
	);

	useEffect(() => {
		if (consentStatistics) {
			Sentry.init({
				dsn: "https://ce54c161cbbd4c36b891bbbc33201dde@o1137486.ingest.sentry.io/6260380",
				environment: process.env.SENTRY_ENVIRONMENT,
				integrations: [Sentry.browserTracingIntegration(), Sentry.extraErrorDataIntegration(), Sentry.replayIntegration()],
				tracesSampleRate: 1,
				ignoreErrors: [/Network Error/i, /Fetch Error/i],
			});
		} else {
			Sentry.close(0);
		}
	}, [consentStatistics]);

	useEffect(() => {
		userManager.onUserLogout.add(resetServicesAndManagers);
		return () => userManager.onUserLogout.remove(resetServicesAndManagers);
	}, []);
	useEffect(() => {
		if (!resolveInactivityEnabled()) return; // feature gate
		const controller = attachInactivityLogout(userManager, {
			enabled: resolveInactivityEnabled(),
			timeoutMs: resolveInactivityTimeoutMs(),
		});
		if (currentUser) {
			controller.start();
		} else {
			controller.stop();
		}
		return () => controller.stop();
	}, [currentUser]);

	const [showErrorScreen, errorType] = useMemo(() => {
		if (!translationsStatus.available) {
			return [true, hasOwnProperty(errorObject, "status") ? ErrorType.SERVER : ErrorType.NETWORK];
		}
		if (!serverConfigStatus.available) {
			return [true, isApiError(serverConfigStatus.error) ? ErrorType.SERVER : ErrorType.NETWORK];
		}
		return [false, ErrorType.SERVER];
	}, [errorObject, serverConfigStatus.available, serverConfigStatus.error, translationsStatus.available]);

	return showErrorScreen ? (
		<ServerErrorView refresh={initializeServicesAndManagers} errorType={errorType} />
	) : (
		<AppRouter />
	);
};

const globalStyles = css`
	html,
	body {
		padding: 0;
		margin: 0;
		min-height: 100vh;
		display: flex;
		flex-direction: column;
		background-color: ${colors.primaryBackground};
		font-family: ${fonts.body};
		color: ${colors.black};
		font-weight: 400;
		font-size: 1rem;
	}

	a {
		text-decoration: none;
	}

	* {
		font-family: ${fonts.body};
		text-edge: cap alphabetic;
		leading-trim: both;
	}
`;
